import { useState, useEffect } from 'react';
import { Zap, Sparkles, X } from 'lucide-react';
import { coworkApi, onUpgradeRequired } from '../api/coworkApi';
import type { PlanInfo } from '../api/coworkApi';

/**
 * UpgradeModal — shown when credits are exhausted (402) or opened manually.
 * Displays current plan info and two upgrade options (Startup / Growth).
 */
export function UpgradeModal({
  open: controlledOpen,
  onOpenChange,
  planInfo,
}: {
  /** Externally controlled open state (optional). If omitted, listens for 402 events. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  planInfo?: PlanInfo | null;
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [loading, setLoading] = useState<'startup' | 'growth' | null>(null);

  const isOpen = controlledOpen ?? internalOpen;
  const setOpen = (v: boolean) => {
    setInternalOpen(v);
    onOpenChange?.(v);
  };

  // Listen for global 402 events
  useEffect(() => {
    return onUpgradeRequired(() => {
      setOpen(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpgrade = async (plan: 'startup' | 'growth') => {
    try {
      setLoading(plan);
      const { url } = await coworkApi.stripe.createCheckout(plan);
      window.location.href = url;
    } catch (err) {
      console.error('[UpgradeModal] Checkout failed:', err);
      setLoading(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => setOpen(false)}
      />

      {/* Modal */}
      <div
        className="relative bg-white rounded-[24px] w-full max-w-[520px] overflow-hidden animate-in fade-in zoom-in-95 duration-200"
        style={{ boxShadow: '0 24px 80px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)' }}
      >
        {/* Close button */}
        <button
          onClick={() => setOpen(false)}
          className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-[#98a2b3] hover:text-[#344054] hover:bg-gray-100 transition-colors z-10"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header */}
        <div className="px-8 pt-8 pb-4 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#f8f5ff] flex items-center justify-center mx-auto mb-4">
            <Zap className="w-7 h-7 text-[#be43ff]" />
          </div>
          <h3
            className="text-[#101828] text-lg mb-1.5"
            style={{ fontWeight: 600 }}
          >
            {planInfo?.plan === 'trial_expired'
              ? 'Your trial has ended'
              : (planInfo?.creditsRemaining ?? 0) <= 0
                ? "You've run out of credits"
                : 'Upgrade your plan'}
          </h3>
          <p className="text-[#667085] text-sm max-w-[360px] mx-auto">
            {planInfo?.plan === 'trial_expired'
              ? `Your trial plan has expired. You still have ${planInfo.creditsRemaining} credits — upgrade to keep your agents running.`
              : planInfo
                ? `Your ${planInfo.plan} plan has ${planInfo.creditsRemaining} of ${planInfo.creditsTotal} credits remaining.`
                : 'Upgrade your plan to keep your AI agents running.'}
          </p>
        </div>

        {/* Plans */}
        <div className="px-6 pb-6 grid grid-cols-2 gap-3">
          {/* Startup */}
          <div className="rounded-2xl border border-gray-200 p-5 flex flex-col relative hover:border-[#be43ff]/30 transition-colors">
            <div className="text-[13px] text-[#344054] mb-1" style={{ fontWeight: 600 }}>
              Startup
            </div>
            <div className="flex items-baseline gap-1 mb-3">
              <span className="text-[24px] text-[#101828]" style={{ fontWeight: 700 }}>
                &euro;29
              </span>
              <span className="text-[13px] text-[#98a2b3]">/mo</span>
            </div>
            <div className="text-[12px] text-[#667085] mb-4">
              <span style={{ fontWeight: 500 }}>500 credits</span> per month
            </div>
            <button
              onClick={() => handleUpgrade('startup')}
              disabled={loading !== null}
              className="mt-auto w-full py-2.5 rounded-xl border border-gray-200 text-[13px] text-[#344054] hover:bg-[#f9fafb] transition-all active:scale-[0.97] disabled:opacity-50"
              style={{ fontWeight: 600 }}
            >
              {loading === 'startup' ? 'Redirecting...' : 'Upgrade'}
            </button>
          </div>

          {/* Growth (recommended) */}
          <div
            className="rounded-2xl border-2 border-[#be43ff]/40 p-5 flex flex-col relative bg-[#fdf8ff]"
          >
            <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-[#be43ff] text-white text-[10px]" style={{ fontWeight: 600 }}>
                <Sparkles className="w-3 h-3" />
                Recommended
              </span>
            </div>
            <div className="text-[13px] text-[#344054] mb-1 mt-1" style={{ fontWeight: 600 }}>
              Growth
            </div>
            <div className="flex items-baseline gap-1 mb-3">
              <span className="text-[24px] text-[#101828]" style={{ fontWeight: 700 }}>
                &euro;99
              </span>
              <span className="text-[13px] text-[#98a2b3]">/mo</span>
            </div>
            <div className="text-[12px] text-[#667085] mb-4">
              <span style={{ fontWeight: 500 }}>3,000 credits</span> per month
            </div>
            <button
              onClick={() => handleUpgrade('growth')}
              disabled={loading !== null}
              className="mt-auto w-full py-2.5 rounded-xl bg-[#be43ff] text-white text-[13px] hover:bg-[#a830e0] transition-all active:scale-[0.97] disabled:opacity-50"
              style={{ fontWeight: 600 }}
            >
              {loading === 'growth' ? 'Redirecting...' : 'Upgrade'}
            </button>
          </div>
        </div>

        {/* Dismiss */}
        <div className="px-8 pb-6 text-center">
          <button
            onClick={() => setOpen(false)}
            className="text-[13px] text-[#98a2b3] hover:text-[#667085] transition-colors"
            style={{ fontWeight: 500 }}
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
